<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Page Header -->
      <v-row class="pa-7" justify="center">
        <h1 class="nexus-title">EDIT OLD JOB REPORT</h1>
      </v-row>

      <!-- Form Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="10" class="pa-0">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-8">
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64"/>
              </v-overlay>

              <!-- Locked Status Alert -->
              <v-alert
                v-if="lockReport"
                type="info"
                dense
                outlined
                class="mb-4"
              >
                <v-icon slot="prepend">mdi-lock</v-icon>
                This report is locked - Job status is {{ jobStatus }}
              </v-alert>

              <!-- Section Header -->
              <div class="section-header mb-6">
                <h2 class="section-title">JOB INFORMATION</h2>
                <div class="section-divider"></div>
              </div>

              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Job Selection Row -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="jobNumber"
                      label="Job Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-numeric"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-select
                      v-model="jobStatus"
                      :items="statusOptions"
                      label="Job Status"
                      dense
                      outlined
                      prepend-inner-icon="mdi-information"
                      :readonly="!isStatusEditable"
                      :disabled="!isStatusEditable"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Customer Info Row -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="customerName"
                      label="Customer Name"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-group"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="contactPerson"
                      label="Contact Person"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Job Details Row -->
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="sealType"
                      label="Seal Type"
                      dense
                      outlined
                      prepend-inner-icon="mdi-cog"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="serialNumber"
                      label="Serial Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-barcode"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="shaftSize"
                      label="Shaft Size"
                      dense
                      outlined
                      prepend-inner-icon="mdi-ruler"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Personnel Row -->
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="engineer"
                      label="Engineer"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-hard-hat"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="technician"
                      label="Technician"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-wrench"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="salesperson"
                      label="Salesperson"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-tie"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Date and Priority Row -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="inspectionDate"
                      label="Inspection Date"
                      dense
                      outlined
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="priority"
                      label="Priority"
                      dense
                      outlined
                      prepend-inner-icon="mdi-alert"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Job Description -->
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="jobDescription"
                      label="Job Description"
                      outlined
                      dense
                      prepend-inner-icon="mdi-text"
                      rows="3"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Labour Items Section -->
                <div class="section-header mb-6 mt-8">
                  <h2 class="section-title">LABOUR ITEMS</h2>
                  <div class="section-divider"></div>
                </div>

                <v-simple-table class="nexus-table mb-4">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Qty</th>
                        <th class="text-left">Part Size</th>
                        <th class="text-left">Description</th>
                        <th class="text-left">Job Category</th>
                        <th class="text-left">Markup</th>
                        <th class="text-left">Unit Price</th>
                        <th class="text-left">Total Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(labourItem, index) in labourItems" :key="index">
                        <td>{{ labourItem.quantity }}</td>
                        <td>{{ labourItem.partSize }}</td>
                        <td>{{ labourItem.description }}</td>
                        <td>{{ labourItem.jobCategory }}</td>
                        <td>{{ labourItem.markUp }}</td>
                        <td>R {{ parseFloat(labourItem.unitPrice || 0).toFixed(2) }}</td>
                        <td>R {{ parseFloat(labourItem.totalPrice || 0).toFixed(2) }}</td>
                      </tr>
                      <tr v-if="!labourItems.length">
                        <td colspan="7" class="text-center grey--text">No labour items</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <v-row justify="end">
                  <v-col cols="12" md="3" class="pa-2">
                    <v-text-field
                      :value="'R ' + parseFloat(labourSubTotal || 0).toFixed(2)"
                      label="Labour Subtotal"
                      dense
                      outlined
                      prepend-inner-icon="mdi-cash-multiple"
                      class="nexus-input"
                      readonly
                    />
                  </v-col>
                </v-row>

                <!-- Stock Items Section -->
                <div class="section-header mb-6 mt-8">
                  <h2 class="section-title">STOCK/PARTS ITEMS</h2>
                  <div class="section-divider"></div>
                </div>

                <v-simple-table class="nexus-table mb-4">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Qty</th>
                        <th class="text-left">Code</th>
                        <th class="text-left">Drawing Number</th>
                        <th class="text-left">Supplier</th>
                        <th class="text-left">Lead Time</th>
                        <th class="text-left">Stock Item</th>
                        <th class="text-left">Cost Price</th>
                        <th class="text-left">Markup</th>
                        <th class="text-left">Unit Price</th>
                        <th class="text-left">Total Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(stockItem, index) in stockItems" :key="index">
                        <td>{{ stockItem.quantity }}</td>
                        <td>{{ stockItem.code }}</td>
                        <td>{{ stockItem.drawingNumber }}</td>
                        <td>{{ getSupplierName(stockItem.supplier) }}</td>
                        <td>{{ stockItem.leadTime }}</td>
                        <td>{{ stockItem.jobCategory }}</td>
                        <td>R {{ parseFloat(stockItem.costPrice || 0).toFixed(2) }}</td>
                        <td>{{ stockItem.markUp }}</td>
                        <td>R {{ parseFloat(stockItem.unitPrice || 0).toFixed(2) }}</td>
                        <td>R {{ parseFloat(stockItem.totalPrice || 0).toFixed(2) }}</td>
                      </tr>
                      <tr v-if="!stockItems.length">
                        <td colspan="10" class="text-center grey--text">No stock items</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <v-row justify="end">
                  <v-col cols="12" md="3" class="pa-2">
                    <v-text-field
                      :value="'R ' + parseFloat(stockSubTotal || 0).toFixed(2)"
                      label="Stock/Parts Subtotal"
                      dense
                      outlined
                      prepend-inner-icon="mdi-cash-multiple"
                      class="nexus-input"
                      readonly
                    />
                  </v-col>
                </v-row>

                <!-- Grand Total -->
                <v-row justify="end">
                  <v-col cols="12" md="3" class="pa-2">
                    <v-text-field
                      :value="'R ' + parseFloat(grandTotal || 0).toFixed(2)"
                      label="Grand Total"
                      dense
                      outlined
                      prepend-inner-icon="mdi-cash-multiple"
                      class="nexus-input font-weight-bold"
                      readonly
                    />
                  </v-col>
                </v-row>

                <!-- Inspection Checklist Section -->
                <div class="section-header mb-6 mt-8">
                  <h2 class="section-title">INSPECTION CHECKLIST</h2>
                  <div class="section-divider"></div>
                </div>

                <!-- Checklist Alert -->
                <v-alert
                  v-if="!checklistData.length"
                  type="warning"
                  dense
                  outlined
                  class="mb-4"
                >
                  <v-icon slot="prepend">mdi-alert</v-icon>
                  No inspection checklist found for this job.
                </v-alert>

                <!-- Checklist Display -->
                <div v-if="checklistData.length" class="checklist-container">
                  <v-expansion-panels v-model="expandedPanels" multiple>
                    <v-expansion-panel
                      v-for="(section, sectionIndex) in checklistData"
                      :key="sectionIndex"
                      class="nexus-expansion-panel"
                    >
                      <v-expansion-panel-header class="nexus-panel-header">
                        <div class="d-flex align-center">
                          <v-icon left color="#475569">mdi-clipboard-check</v-icon>
                          <span class="font-weight-bold">{{ section.sectionTitle }}</span>
                          <v-chip small class="ml-3" color="primary" outlined>
                            {{ section.items.length }} items
                          </v-chip>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="nexus-panel-content">
                        <v-simple-table class="nexus-table mt-4">
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-left" style="width: 60%">Item</th>
                                <th class="text-left" style="width: 20%">Response</th>
                                <th class="text-left" style="width: 20%">Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(item, itemIndex) in section.items" :key="itemIndex">
                                <td>{{ item.label }}</td>
                                <td>
                                  <v-chip
                                    :color="item.checked === 'Yes' ? 'success' : 'error'"
                                    small
                                    dark
                                  >
                                    {{ item.checked }}
                                  </v-chip>
                                </td>
                                <td>
                                  <div v-if="item.checked === 'Yes'" class="details-text">
                                    <div v-if="item.seal1">{{ item.seal1 }}</div>
                                    <div v-if="item.seal2">{{ item.seal2 }}</div>
                                    <div v-if="item.seal3">{{ item.seal3 }}</div>
                                    <div v-if="item.seal4">{{ item.seal4 }}</div>
                                    <div v-if="item.jobNumberField">Job #: {{ item.jobNumberField }}</div>
                                    <div v-if="item.settingclipfield">{{ item.settingclipfield }}</div>
                                    <div v-if="item.oringMaterial">Material: {{ item.oringMaterial }}</div>
                                    <div v-if="item.oringdamage">{{ item.oringdamage }}</div>
                                    <div v-if="item.oringSize">Size: {{ item.oringSize }}</div>
                                  </div>
                                  <span v-else class="grey--text">-</span>
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>

                        <!-- Section Photos -->
                        <div v-if="section.photos && section.photos.length" class="mt-4">
                          <h4 class="mb-3" style="color: #475569;">
                            <v-icon left color="#475569">mdi-image-multiple</v-icon>
                            Photos ({{ section.photos.length }})
                          </h4>
                          <v-row>
                            <v-col
                              v-for="(photo, photoIndex) in section.photos"
                              :key="photoIndex"
                              cols="12"
                              sm="6"
                              md="4"
                            >
                              <v-card class="photo-card" @click="openPhotoDialog(photo)">
                                <v-img
                                  :src="photo"
                                  aspect-ratio="1"
                                  class="grey lighten-2"
                                >
                                  <template v-slot:placeholder>
                                    <v-row class="fill-height ma-0" align="center" justify="center">
                                      <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                                    </v-row>
                                  </template>
                                </v-img>
                                <v-card-actions class="pa-2">
                                  <v-btn small text color="primary">
                                    <v-icon small left>mdi-magnify</v-icon>
                                    View
                                  </v-btn>
                                </v-card-actions>
                              </v-card>
                            </v-col>
                          </v-row>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>

                <!-- Report Notes Section -->
                <div class="section-header mb-6 mt-8">
                  <h2 class="section-title">REPORT NOTES</h2>
                  <div class="section-divider"></div>
                </div>

                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="reportNotes"
                      label="Additional Notes for Report"
                      outlined
                      dense
                      prepend-inner-icon="mdi-note-text"
                      rows="4"
                      placeholder="Add any additional observations, recommendations, or notes..."
                      :readonly="lockReport"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Action Buttons -->
                <v-row justify="center" class="my-6">
                  <v-col cols="auto">
                    <v-btn
                      large
                      color="primary"
                      class="nexus-btn mr-4"
                      @click="confirmSubmit"
                      :disabled="!valid || loading || lockReport || saving"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      {{ saving ? 'Saving...' : 'Save Changes' }}
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      large
                      outlined
                      color="primary"
                      class="nexus-btn-outline mr-4"
                      @click="previewReport"
                    >
                      <v-icon left>mdi-eye</v-icon>
                      Preview Report
                    </v-btn>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      large
                      outlined
                      color="success"
                      class="nexus-btn-outline"
                      @click="generatePDF"
                      :disabled="loading || generating"
                    >
                      <v-icon left>mdi-file-pdf-box</v-icon>
                      {{ generating ? 'Generating...' : 'Download PDF' }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Photo Viewer Dialog -->
    <v-dialog v-model="photoDialog" max-width="800px">
      <v-card class="nexus-dialog">
        <v-toolbar dark color="#475569">
          <v-toolbar-title>Photo</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="photoDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-img :src="selectedPhoto" max-height="600"></v-img>
      </v-card>
    </v-dialog>

    <!-- Photo Carousel Dialog -->
    <v-dialog v-model="photoCarouselDialog" max-width="800px">
      <v-card>
        <v-card-title class="headline">Job Photos</v-card-title>
        <v-card-text>
          <v-carousel v-if="!loadingPhotos && jobPhotos.length > 0" hide-delimiters>
            <v-carousel-item v-for="(photo, index) in jobPhotos" :key="index">
              <v-img :src="photo" class="carousel-photo" max-height="400px"></v-img>
            </v-carousel-item>
          </v-carousel>
          <div v-if="loadingPhotos" class="d-flex align-center">
            <v-progress-circular indeterminate color="blue"></v-progress-circular>
            <span class="ml-2">Loading photos...</span>
          </div>
          <div v-if="!loadingPhotos && jobPhotos.length === 0">
            <p>No photos available for this job.</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue" dark @click="downloadPhotos">
            <v-icon left>mdi-download</v-icon>Download All Photos
          </v-btn>
          <v-btn color="red" dark @click="photoCarouselDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Report Preview Dialog -->
    <v-dialog v-model="previewDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-app-bar dark color="#475569">
          <v-toolbar-title>Report Preview - Job #{{ jobNumber }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="generatePDF" title="Download PDF">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click="openAndDownloadPhotos" title="Download All Photos">
            <v-icon>mdi-image-multiple</v-icon>
          </v-btn>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-app-bar>

        <v-card-text class="pa-6" id="report-content">
          <!-- Job Information Section -->
          <v-card class="mb-4 elevation-2">
            <v-card-title style="background: #475569; color: white;">
              Job Information
            </v-card-title>
            <v-card-text class="pt-4">
              <div class="modal-grid">
                <div class="modal-field">
                  <div class="modal-field-label">Job Number</div>
                  <div class="modal-field-value">{{ jobNumber }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Seal Type</div>
                  <div class="modal-field-value">{{ sealType }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Serial Number</div>
                  <div class="modal-field-value">{{ serialNumber }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Customer</div>
                  <div class="modal-field-value">{{ customerName }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Contact Person</div>
                  <div class="modal-field-value">{{ contactPerson }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Status</div>
                  <div class="modal-field-value">{{ jobStatus }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Engineer</div>
                  <div class="modal-field-value">{{ engineer }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Technician</div>
                  <div class="modal-field-value">{{ technician }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Salesperson</div>
                  <div class="modal-field-value">{{ salesperson }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Inspection Date</div>
                  <div class="modal-field-value">{{ inspectionDate }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Shaft Size</div>
                  <div class="modal-field-value">{{ shaftSize }}</div>
                </div>
                <div class="modal-field">
                  <div class="modal-field-label">Priority</div>
                  <div class="modal-field-value">{{ priority }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Labour Items Section -->
          <v-card class="mb-4 elevation-2" v-if="labourItems.length">
            <v-card-title style="background: #475569; color: white;">
              Labour Items
            </v-card-title>
            <v-card-text class="pt-4">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Qty</th>
                      <th class="text-left">Part Size</th>
                      <th class="text-left">Description</th>
                      <th class="text-left">Category</th>
                      <th class="text-left">Markup</th>
                      <th class="text-left">Unit Price</th>
                      <th class="text-left">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in labourItems" :key="index">
                      <td>{{ item.quantity }}</td>
                      <td>{{ item.partSize }}</td>
                      <td>{{ item.description }}</td>
                      <td>{{ item.jobCategory }}</td>
                      <td>{{ item.markUp }}</td>
                      <td>R {{ parseFloat(item.unitPrice || 0).toFixed(2) }}</td>
                      <td>R {{ parseFloat(item.totalPrice || 0).toFixed(2) }}</td>
                    </tr>
                    <tr class="font-weight-bold">
                      <td colspan="6" class="text-right">Labour Subtotal:</td>
                      <td>R {{ parseFloat(labourSubTotal || 0).toFixed(2) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- Stock Items Section -->
          <v-card class="mb-4 elevation-2" v-if="stockItems.length">
            <v-card-title style="background: #475569; color: white;">
              Stock/Parts Items
            </v-card-title>
            <v-card-text class="pt-4">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Qty</th>
                      <th class="text-left">Code</th>
                      <th class="text-left">Drawing #</th>
                      <th class="text-left">Supplier</th>
                      <th class="text-left">Lead Time</th>
                      <th class="text-left">Category</th>
                      <th class="text-left">Cost</th>
                      <th class="text-left">Markup</th>
                      <th class="text-left">Unit Price</th>
                      <th class="text-left">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in stockItems" :key="index">
                      <td>{{ item.quantity }}</td>
                      <td>{{ item.code }}</td>
                      <td>{{ item.drawingNumber }}</td>
                      <td>{{ getSupplierName(item.supplier) }}</td>
                      <td>{{ item.leadTime }}</td>
                      <td>{{ item.jobCategory }}</td>
                      <td>R {{ parseFloat(item.costPrice || 0).toFixed(2) }}</td>
                      <td>{{ item.markUp }}</td>
                      <td>R {{ parseFloat(item.unitPrice || 0).toFixed(2) }}</td>
                      <td>R {{ parseFloat(item.totalPrice || 0).toFixed(2) }}</td>
                    </tr>
                    <tr class="font-weight-bold">
                      <td colspan="9" class="text-right">Stock Subtotal:</td>
                      <td>R {{ parseFloat(stockSubTotal || 0).toFixed(2) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- Grand Total -->
          <v-card class="mb-4 elevation-2" v-if="labourItems.length || stockItems.length">
            <v-card-text class="pt-4">
              <v-row>
                <v-col cols="12" class="text-right">
                  <h2 style="color: #475569;">Grand Total: R {{ parseFloat(grandTotal || 0).toFixed(2) }}</h2>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Inspection Checklist Section -->
          <v-card class="mb-4 elevation-2" v-if="checklistData.length">
            <v-card-title style="background: #475569; color: white;">
              Inspection Checklist
            </v-card-title>
            <v-card-text class="pt-4">
              <div v-for="(section, sectionIndex) in checklistData" :key="sectionIndex" class="mb-6">
                <h3 class="section-header mb-3" style="color: #475569;">
                  {{ section.sectionTitle }}
                </h3>
                <v-divider class="mb-3"></v-divider>
                
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left" style="width: 60%">Item</th>
                        <th class="text-left" style="width: 20%">Response</th>
                        <th class="text-left" style="width: 20%">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, itemIndex) in section.items" :key="itemIndex">
                        <td>{{ item.label }}</td>
                        <td>
                          <v-chip
                            :color="item.checked === 'Yes' ? 'green' : 'red'"
                            small
                            dark
                          >
                            {{ item.checked }}
                          </v-chip>
                        </td>
                        <td>
                          <div v-if="item.checked === 'Yes'" class="details-column">
                            <div v-if="item.seal1">{{ item.seal1 }}</div>
                            <div v-if="item.jobNumberField">Job #: {{ item.jobNumberField }}</div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <!-- Section Photos -->
                <div v-if="section.photos && section.photos.length" class="mt-4">
                  <h4 class="mb-2">Photos</h4>
                  <v-row>
                    <v-col
                      v-for="(photo, photoIndex) in section.photos"
                      :key="photoIndex"
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-img
                        :src="photo"
                        aspect-ratio="1"
                        class="grey lighten-2"
                      >
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Job Description and Notes -->
          <v-card class="mb-4 elevation-2" v-if="jobDescription || reportNotes">
            <v-card-title style="background: #475569; color: white;">
              Additional Information
            </v-card-title>
            <v-card-text class="pt-4">
              <div v-if="jobDescription" class="mb-4">
                <h4 style="color: #475569;" class="mb-2">Job Description</h4>
                <p>{{ jobDescription }}</p>
              </div>
              <div v-if="reportNotes">
                <h4 style="color: #475569;" class="mb-2">Report Notes</h4>
                <p>{{ reportNotes }}</p>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialogs -->
    <v-dialog v-model="confirmationDialog.visible" max-width="400">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">{{ confirmationDialog.title }}</v-card-title>
        <v-card-text>{{ confirmationDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="success" text @click="confirmationDialog.confirm">Yes</v-btn>
          <v-btn color="grey" text @click="confirmationDialog.visible=false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay for PDF Generation -->
    <v-overlay :value="generating">
      <v-progress-circular indeterminate size="70" color="white"></v-progress-circular>
      <div class="mt-4 white--text">Generating PDF Report...</div>
    </v-overlay>

    <!-- Snackbars -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc
} from "firebase/firestore";
import { firebaseApp, auth } from "@/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const db = getFirestore(firebaseApp);

export default {
  name: "EditOldJob",
  data() {
    return {
      loading: false,
      generating: false,
      saving: false,
      valid: false,
      
      // Job ID
      selectedJobId: null,
      
      // Job data
      jobNumber: "",
      customerName: "",
      contactPerson: "",
      sealType: "",
      serialNumber: "",
      shaftSize: "",
      engineer: "",
      technician: "",
      salesperson: "",
      jobStatus: "",
      originalStatus: "",
      inspectionDate: "",
      priority: "",
      jobDescription: "",
      
      // Labour and Stock items
      labourItems: [],
      stockItems: [],
      labourSubTotal: 0,
      stockSubTotal: 0,
      grandTotal: 0,
      
      // Checklist data
      checklistData: [],
      expandedPanels: [],
      
      // Report notes
      reportNotes: "",
      
      // Dialogs
      photoDialog: false,
      photoCarouselDialog: false,
      selectedPhoto: "",
      previewDialog: false,
      confirmationDialog: {
        visible: false,
        title: "",
        message: "",
        confirm: null
      },
      
      // Snackbar
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      
      // User
      loggedInUser: "",
      userRole: "",
      
      // Dropdown data
      customers: [],
      suppliers: [],
      engineers: [],
      technicians: [],
      salespersons: [],
      
      // Status options - simple array of strings
      statusOptions: [
        "Inspection",
        "Costing Complete",
        "Quoted",
        "InProgress",
        "Ready",
        "Completed",
        "Delayed",
        "Canceled"
      ],

      // Photo loading
      jobPhotos: [],
      loadingPhotos: false
    };
  },

  computed: {
    lockReport() {
      return this.originalStatus === "Delivered" || this.originalStatus === "Canceled";
    },
    
    isStatusEditable() {
      // Status is only editable if the job is not Delivered or Canceled
      return !this.lockReport;
    }
  },

  async created() {
    this.loading = true;
    this.selectedJobId = localStorage.getItem('selectedOldJobId') || '';

    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        this.$router.push('/login');
        return;
      }
      
      try {
        this.loggedInUser = user.email;
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          this.userRole = userDoc.data().role || '';
        }
      } catch (e) {
        console.error("Error loading user:", e);
      }
    });

    if (this.selectedJobId) {
      await this.loadJobReport();
      await Promise.all([
        this.fetchCustomers(),
        this.fetchSuppliers(),
        this.fetchEngineers(),
        this.fetchTechnicians(),
        this.fetchSalespersons(),
      ]);
    } else {
      this.showSnackbar("No job selected", "error");
      this.$router.push({ name: 'OldJobs' });
    }
    
    this.loading = false;
  },

  methods: {
    async loadJobReport() {
      try {
        const jobDoc = await getDoc(doc(db, "jobs", this.selectedJobId));
        
        if (jobDoc.exists()) {
          const job = jobDoc.data();
          this.jobNumber = job.jobNumber || "";
          this.customerName = job.customerName || "";
          this.contactPerson = job.customerNumber || "";
          this.sealType = job.jobName || "";
          this.serialNumber = job.serialNumber || "";
          this.shaftSize = job.ShaftSize || "";
          this.engineer = job.engineer?.fullName || "N/A";
          this.technician = job.technician?.fullName || "N/A";
          this.salesperson = job.salesperson?.fullName || "N/A";
          
          // Set job status - ensure it's set properly with a default value
          this.jobStatus = job.status || "Inspection";
          this.originalStatus = job.status || "Inspection";
          
          this.inspectionDate = job.currentDate || "";
          this.priority = job.urgency || "";
          this.jobDescription = job.jobDescription || "";
          this.reportNotes = job.reportNotes || "";
          
          // Load labour and stock items
          this.labourItems = job.labourItems || [];
          this.stockItems = job.stockItems || [];
          this.labourSubTotal = job.labourSubTotal || 0;
          this.stockSubTotal = job.stockSubTotal || 0;
          this.grandTotal = job.grandTotal || 0;
          
          // Only fetch checklist if jobNumber exists and is valid
          if (this.jobNumber) {
            await this.fetchChecklist(this.jobNumber);
          } else {
            console.warn("Job number is empty, skipping checklist fetch");
            this.checklistData = [];
          }
          
          // Expand all panels by default
          this.expandedPanels = this.checklistData.map((_, index) => index);
        } else {
          this.showSnackbar("Job not found", "error");
          this.$router.push({ name: 'OldJobs' });
        }
      } catch (error) {
        console.error("Error loading job report:", error);
        this.showSnackbar("Failed to load job report", "error");
      }
    },

    getSupplierName(supplierId) {
      const supplier = this.suppliers.find(s => s.docId === supplierId);
      return supplier ? supplier.supplierName : supplierId || 'N/A';
    },

    async fetchChecklist(jobNumber) {
      if (!jobNumber) {
        console.warn("Cannot fetch checklist: jobNumber is undefined");
        return;
      }

      try {
        const checklistRef = collection(db, "checklists");
        const q = query(checklistRef, where("jobNumber", "==", String(jobNumber)));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          this.checklistData = docData.checklist || [];
        } else {
          this.checklistData = [];
          console.log("No checklist found for job number:", jobNumber);
        }
      } catch (error) {
        console.error("Error fetching checklist:", error);
        this.showSnackbar("Failed to fetch checklist", "error");
        this.checklistData = [];
      }
    },

    async fetchChecklistPhotos(jobNumber) {
      const checklistRef = collection(db, "checklists");
      const q = query(checklistRef, where("jobNumber", "==", jobNumber));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return [];
      const checklist = querySnapshot.docs[0].data().checklist || [];
      return checklist.flatMap(section => section.photos || []);
    },

    async openPhotoCarouselDialog() {
      if (!this.jobNumber) {
        this.showSnackbar("No job number available", "error");
        return;
      }
      this.photoCarouselDialog = true;
      this.loadingPhotos = true;
      try {
        this.jobPhotos = await this.fetchChecklistPhotos(this.jobNumber);
      } catch (e) {
        console.error("Fetch checklist photos error:", e);
        this.showSnackbar("Error fetching checklist photos", "error");
      } finally {
        this.loadingPhotos = false;
      }
    },

    async openAndDownloadPhotos() {
      try {
        await this.openPhotoCarouselDialog();
        if (this.jobPhotos.length) this.downloadPhotos();
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to download photos", "error");
      }
    },

    async downloadPhotos() {
      if (!this.jobPhotos.length) {
        this.showSnackbar("No photos available", "error");
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder(`job_${this.jobNumber}_photos`);
      await Promise.all(this.jobPhotos.map((url, i) =>
        fetch(url).then((r) => r.blob()).then((b) => {
          folder.file(`photo_${i + 1}_${url.split("/").pop().split("?")[0]}`, b);
        })
      ));
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `job_${this.jobNumber}_photos.zip`);
      this.showSnackbar("Photos downloaded successfully", "success");
    },

    async fetchCustomers() {
      try {
        const snap = await getDocs(collection(db, "customers"));
        this.customers = snap.docs.map((d) => ({
          companyName: d.data().companyName || d.data()["Company Name"] || "",
        }));
      } catch (e) {
        console.error("Error fetching customers:", e);
      }
    },

    async fetchSuppliers() {
      try {
        const snap = await getDocs(collection(db, "suppliers"));
        this.suppliers = snap.docs.map((d) => ({
          supplierName: d.data()["Supplier Name"] || "",
          docId: d.id,
        }));
      } catch (e) {
        console.error("Error fetching suppliers:", e);
      }
    },

    async fetchEngineers() {
      try {
        const q = query(collection(db, "users"), where("role", "==", "Engineer"));
        const snap = await getDocs(q);
        this.engineers = snap.docs.map((d) => ({
          fullName: d.data().fullName || "",
          docId: d.id,
        }));
      } catch (e) {
        console.error("Error fetching engineers:", e);
      }
    },

    async fetchTechnicians() {
      try {
        const q = query(collection(db, "users"), where("role", "==", "Technician"));
        const snap = await getDocs(q);
        this.technicians = snap.docs.map((d) => ({
          fullName: d.data().fullName || "",
          docId: d.id,
        }));
      } catch (e) {
        console.error("Error fetching technicians:", e);
      }
    },

    async fetchSalespersons() {
      try {
        const q = query(collection(db, "users"), where("role", "==", "Salesperson"));
        const snap = await getDocs(q);
        this.salespersons = snap.docs.map((d) => ({
          fullName: d.data().fullName || "",
          docId: d.id,
        }));
      } catch (e) {
        console.error("Error fetching salespersons:", e);
      }
    },

    openPhotoDialog(photo) {
      this.selectedPhoto = photo;
      this.photoDialog = true;
    },

    previewReport() {
      if (!this.$refs.form.validate()) return;
      this.previewDialog = true;
    },

    async generatePDF() {
      if (!this.$refs.form.validate()) return;
      
      this.generating = true;
      try {
        // If preview dialog is not open, open it first
        if (!this.previewDialog) {
          this.previewDialog = true;
          await this.$nextTick();
        }

        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const element = document.getElementById("report-content");
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`Old_Job_Report_${this.jobNumber}.pdf`);
        this.showSnackbar("PDF generated successfully!", "success");
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.showSnackbar("Failed to generate PDF", "error");
      } finally {
        this.generating = false;
      }
    },

    confirmSubmit() {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Save",
        message: "Save changes to this old job report?",
        confirm: this.submitForm
      };
    },

    async submitForm() {
      if (!this.$refs.form.validate() || this.saving) return;
      
      this.saving = true;
      this.loading = true;
      
      try {
        await updateDoc(doc(db, 'jobs', this.selectedJobId), {
          status: this.jobStatus,
          reportNotes: this.reportNotes
        });
        
        this.originalStatus = this.jobStatus;
        this.showSnackbar("Report updated successfully!", "success");
      } catch (error) {
        console.error("Error updating report:", error);
        this.showSnackbar(`Failed to update report: ${error.message}`, "error");
      } finally {
        this.loading = false;
        this.saving = false;
        this.confirmationDialog.visible = false;
      }
    },

    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Navigation",
        message: "Are you sure you want to go back? Any unsaved changes will be lost.",
        confirm: this.goBack
      };
    },

    goBack() {
      this.confirmationDialog.visible = false;
      this.$router.go(-1);
    },

    showSnackbar(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

.nexus-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  color: #475569;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  margin: 0;
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
  margin-left: 20px;
  border-radius: 1px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.4) !important;
}

.nexus-btn-outline {
  border: 2px solid #475569 !important;
  color: #475569 !important;
  background: transparent !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.nexus-btn-outline:hover {
  background: #475569 !important;
  color: white !important;
  transform: translateY(-2px) !important;
}

.back-button {
  color: white !important;
  background: rgba(71, 85, 105, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  position: absolute;
  top: 24px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.back-button .v-icon {
  color: white !important;
}

.nexus-dialog {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-dialog-title {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.nexus-table {
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 12px !important;
  overflow: hidden !important;
}

.nexus-table >>> .v-data-table__wrapper {
  border-radius: 12px !important;
}

.nexus-table >>> th {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #475569 !important;
  font-weight: 600 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.checklist-container {
  margin-top: 20px;
}

.nexus-expansion-panel {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  margin-bottom: 16px !important;
  overflow: hidden;
}

.nexus-panel-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.nexus-panel-content {
  background: white !important;
}

.photo-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px !important;
  overflow: hidden;
}

.photo-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(71, 85, 105, 0.15) !important;
}

.details-text div {
  margin-bottom: 4px;
  font-size: 0.9rem;
  color: #475569;
}

.details-column div {
  margin-bottom: 4px;
  font-size: 0.9rem;
}

.modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-field-label {
  color: #475569;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modal-field-value {
  color: #2d3748;
  font-size: 14px;
  word-break: break-word;
}

.carousel-photo {
  object-fit: contain;
}

.d-flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.ml-2 {
  margin-left: 8px;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}

/* Form validation styles */
.nexus-input >>> .v-messages__message {
  color: #dc2626 !important;
  font-weight: 500 !important;
}

.nexus-input >>> .v-input--has-state .v-input__slot {
  border-color: #dc2626 !important;
}

/* Loading overlay */
.v-overlay >>> .v-overlay__content {
  background: rgba(15, 23, 42, 0.8) !important;
  backdrop-filter: blur(4px) !important;
  border-radius: 16px !important;
  padding: 30px !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .nexus-title {
    font-size: 1.75rem;
    letter-spacing: 1px;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
  
  .nexus-card >>> .v-card__text {
    padding: 20px !important;
  }
  
  .modal-grid {
    grid-template-columns: 1fr;
  }
}

@media print {
  .v-app-bar,
  .v-btn {
    display: none !important;
  }
}
</style>